import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity, FlatList } from "react-native";
import { AntDesign } from "@expo/vector-icons";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/useTranslation";
import { fetchRejectedHistoryChinaOrder } from "../../../redux/dispatcher/chinaTrackerDispatcher";
import Spinner from "../../../components/shared/Spinner";
import { useAppTheme } from "../../../hooks/useAppTheme";


interface RejectHistoryItem {
  _id: string;
  key: string;
  orderNumber: number;
  subject: "NEW_DUE_DATE" | "NEW_ORDER_QTY" | "NEW_SHIPPING_DEADLINE";
  dueDate?: Date;
  orderQty?: number;
  shippingDeadline?: Date;
  message: string;
  createdBy: any;
  createdByName: any;
  assignTo?: any;
  assignToName: any;
  assignedstatus: "TO-DO" | "IN-PROGRESS" | "DONE" | "REVIEW";
  createdAt: Date;
  updatedAt: Date;
}

interface RejectHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  order: any;
}

const RejectHistoryModal: React.FC<RejectHistoryModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  const dispatch = useDispatch<any>();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const { t } = useTranslation();
  const { rejectedHistory: rejectHistory, loadingRejectedHistory } =
    useSelector((state: any) => state.chinaTracker);

  const { colors: themeColors } = useAppTheme();
  const styles = createStyles(themeColors);

  useEffect(() => {
    dispatch(fetchRejectedHistoryChinaOrder(order.Key));
  }, [order]);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "95%";
    if (isMediumDevice) return "85%";
    return "80%";
  };

  // Format subject for display
  const formatSubject = (subject: string) => {
    switch (subject) {
      case "NEW_DUE_DATE":
        return "New Due Date";
      case "NEW_ORDER_QTY":
        return "New Order Quantity";
      case "NEW_SHIPPING_DEADLINE":
        return "New Shipping Deadline";
      default:
        return subject;
    }
  };

  // Format status for display
  const getStatusColor = (status: string) => {
    switch (status) {
      case "TO-DO":
        return "#FF6B6B";
      case "IN-PROGRESS":
        return "#4ECDC4";
      case "DONE":
        return "#45B7D1";
      case "REVIEW":
        return "#FFA07A";
      default:
        return themeColors.textSecondary;
    }
  };

  // Format date for display
  const formatDate = (date: Date | string) => {
    if (!date) return "N/A";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format datetime for display
  const formatDateTime = (date: Date | string) => {
    if (!date) return "N/A";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Render individual reject history item
  const renderHistoryItem = ({
    item,
    index,
  }: {
    item: RejectHistoryItem;
    index: number;
  }) => (
    <View
      style={[styles.historyItem, isSmallDevice && styles.historyItemSmall]}
    >
      <View style={styles.historyHeader}>
        <View style={styles.headerLeft}>
          <Text
            style={[styles.historyIndex, isSmallDevice && styles.textSmall]}
          >
            #{index + 1}
          </Text>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: getStatusColor(item.assignedstatus) },
            ]}
          >
            <Text
              style={[
                styles.statusText,
                isSmallDevice && styles.statusTextSmall,
              ]}
            >
              {item.assignedstatus}
            </Text>
          </View>
        </View>
        <Text style={[styles.dateText, isSmallDevice && styles.dateTextSmall]}>
          {formatDateTime(item.createdAt)}
        </Text>
      </View>

      <View style={styles.historyContent}>
        <View style={styles.subjectContainer}>
          <Text
            style={[styles.subjectLabel, isSmallDevice && styles.textSmall]}
          >
            Subject:
          </Text>
          <Text
            style={[styles.subjectValue, isSmallDevice && styles.textSmall]}
          >
            {formatSubject(item.subject)}
          </Text>
        </View>

        {/* Show relevant field based on subject */}
        {item.subject === "NEW_DUE_DATE" && item.dueDate && (
          <View style={styles.detailRow}>
            <Text
              style={[styles.detailLabel, isSmallDevice && styles.textSmall]}
            >
              New Due Date:
            </Text>
            <Text
              style={[styles.detailValue, isSmallDevice && styles.textSmall]}
            >
              {formatDate(item.dueDate)}
            </Text>
          </View>
        )}

        {item.subject === "NEW_ORDER_QTY" && item.orderQty && (
          <View style={styles.detailRow}>
            <Text
              style={[styles.detailLabel, isSmallDevice && styles.textSmall]}
            >
              New Order Qty:
            </Text>
            <Text
              style={[styles.detailValue, isSmallDevice && styles.textSmall]}
            >
              {item.orderQty} Boxes
            </Text>
          </View>
        )}

        {item.subject === "NEW_SHIPPING_DEADLINE" && item.shippingDeadline && (
          <View style={styles.detailRow}>
            <Text
              style={[styles.detailLabel, isSmallDevice && styles.textSmall]}
            >
              New Shipping Deadline:
            </Text>
            <Text
              style={[styles.detailValue, isSmallDevice && styles.textSmall]}
            >
              {formatDate(item.shippingDeadline)}
            </Text>
          </View>
        )}

        <View style={styles.messageContainer}>
          <Text
            style={[styles.messageLabel, isSmallDevice && styles.textSmall]}
          >
            Message:
          </Text>
          <Text style={[styles.messageText, isSmallDevice && styles.textSmall]}>
            {item.message}
          </Text>
        </View>

        <View style={styles.userInfo}>
          <Text
            style={[styles.createdByText, isSmallDevice && styles.textSmall]}
          >
            Created by: {item.createdByName || "Unknown User"}
          </Text>
          {item.assignToName && (
            <Text
              style={[styles.assignedToText, isSmallDevice && styles.textSmall]}
            >
              Assigned to: {item.assignToName}
            </Text>
          )}
        </View>
      </View>
    </View>
  );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <AntDesign name="inbox" size={64} color={themeColors.border} />
      <Text style={[styles.emptyText, isSmallDevice && styles.emptyTextSmall]}>
        No rejection history found for this order
      </Text>
      <Text
        style={[styles.emptySubText, isSmallDevice && styles.emptySubTextSmall]}
      >
        This order has not been rejected yet
      </Text>
    </View>
  );

  return (
    <Modal visible={visible} transparent animationType="slide">
      <View style={styles.overlay}>
        <View style={[styles.modalContainer, { width: getModalWidth() }]}>
          {loadingRejectedHistory ? (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          ) : (
            <View>
              {/* Header */}
              <View style={styles.titleContainer}>
                <View style={styles.headerRow}>
                  <TouchableOpacity onPress={onClose} style={styles.backButton}>
                    <AntDesign name="arrowleft" size={24} color="black" />
                  </TouchableOpacity>
                  <View style={styles.titleContent}>
                    <Text
                      style={[
                        styles.titleText,
                        isSmallDevice && styles.titleTextSmall,
                      ]}
                    >
                      Rejection History
                    </Text>
                    <Text
                      style={[
                        styles.orderNumberText,
                        isSmallDevice && styles.orderNumberTextSmall,
                      ]}
                    >
                      Order: {order.OrderNumber1}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Content */}
              <View style={styles.contentContainer}>
                {rejectHistory?.length > 0 ? (
                  <>
                    <View style={styles.summaryContainer}>
                      <Text
                        style={[
                          styles.summaryText,
                          isSmallDevice && styles.summaryTextSmall,
                        ]}
                      >
                        Total Rejections: {rejectHistory.length}
                      </Text>
                    </View>
                    <FlatList
                      data={rejectHistory}
                      renderItem={renderHistoryItem}
                      keyExtractor={(item) => item._id}
                      showsVerticalScrollIndicator={false}
                      contentContainerStyle={styles.listContainer}
                    />
                  </>
                ) : (
                  renderEmptyState()
                )}
              </View>

              {/* Footer */}
              <View style={styles.footer}>
                <TouchableOpacity style={styles.closeButton} onPress={onClose}>
                  <Text
                    style={[
                      styles.closeButtonText,
                      isSmallDevice && styles.closeButtonTextSmall,
                    ]}
                  >
                    {t("close")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

function createStyles(colors: any) {
  return StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  modalContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    maxHeight: "90%",
    elevation: 10,
    shadowColor: colors.text,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  titleContainer: {
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: "#f8f9fa",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  backButton: {
    marginRight: 15,
    padding: 5,
  },
  titleContent: {
    flex: 1,
  },
  titleText: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text,
  },
  titleTextSmall: {
    fontSize: 16,
  },
  orderNumberText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 2,
  },
  orderNumberTextSmall: {
    fontSize: 12,
  },
  contentContainer: {
    flex: 1,
    paddingHorizontal: 20,
  },
  summaryContainer: {
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
  },
  summaryText: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.text,
  },
  summaryTextSmall: {
    fontSize: 14,
  },
  listContainer: {
    paddingVertical: 10,
  },
  historyItem: {
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
    padding: 15,
    marginVertical: 5,
    borderLeftWidth: 4,
    borderLeftColor: "#007bff",
  },
  historyItemSmall: {
    padding: 12,
    marginVertical: 4,
  },
  historyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  historyIndex: {
    fontSize: 16,
    fontWeight: "bold",
    color: colors.text,
    marginRight: 10,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
  },
  statusTextSmall: {
    fontSize: 10,
  },
  dateText: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: "italic",
  },
  dateTextSmall: {
    fontSize: 10,
  },
  historyContent: {
    gap: 8,
  },
  subjectContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  subjectLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.text,
    marginRight: 8,
  },
  subjectValue: {
    fontSize: 14,
    color: "#007bff",
    fontWeight: "500",
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  detailLabel: {
    fontSize: 13,
    fontWeight: "500",
    color: colors.textSecondary,
    marginRight: 8,
  },
  detailValue: {
    fontSize: 13,
    color: colors.text,
    fontWeight: "500",
  },
  messageContainer: {
    marginTop: 5,
  },
  messageLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: colors.text,
    marginBottom: 3,
  },
  messageText: {
    fontSize: 13,
    color: "#555",
    lineHeight: 18,
    fontStyle: "italic",
  },
  userInfo: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
  },
  createdByText: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  assignedToText: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 50,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: "600",
    color: colors.textSecondary,
    marginTop: 15,
    textAlign: "center",
  },
  emptyTextSmall: {
    fontSize: 16,
  },
  emptySubText: {
    fontSize: 14,
    color: "#999",
    marginTop: 5,
    textAlign: "center",
  },
  emptySubTextSmall: {
    fontSize: 12,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
    paddingHorizontal: 20,
    paddingVertical: 15,
    alignItems: "center",
  },
  closeButton: {
    backgroundColor: "#6c757d",
    paddingHorizontal: 30,
    paddingVertical: 12,
    borderRadius: 6,
    minWidth: 100,
    alignItems: "center",
  },
  closeButtonText: {
    color: colors.surface,
    fontSize: 16,
    fontWeight: "600",
  },
  closeButtonTextSmall: {
    fontSize: 14,
  },
  textSmall: {
    fontSize: 12,
  },
});
}

export default RejectHistoryModal;
